import {ComponentStore} from '@ngrx/component-store';
import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {AppState} from '../store';
import {Actions, ofType} from '@ngrx/effects';
import {ChartStoreState} from './chart';
import {Observable, of, Subscription, switchMap, withLatestFrom} from 'rxjs';
import {QuickDashboardService} from '../../services/quick-dashboard.service';
import {catchError, filter, tap} from 'rxjs/operators';
import {initSiteSuccess} from '../init/init.actions';
import {AdRequestOptions, AdRPeriod, AdRReport} from '../../interfaces/ad-reports';
import {LocalStorage} from '@ngx-pwa/local-storage';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MetricComponentObject, MetricComponentObjects} from '../../classes/metric-component-object';
import {selectSite, selectUser} from '../init/init.selectors';
import {Serializer} from '../../interfaces/serializer';
import {Site} from '../../interfaces/site';
import {showSnackbar} from '../snackbar/snackbar.actions';
import {TypedAction} from '@ngrx/store/src/models';
import {PeriodsService} from '../../services/periods.service';
import {DataSetMetric, Metric} from '../../interfaces/metrics';
import {FormValidators} from '../../validators/form-validators';
import {ReportStore} from '../report/report.store';
import {EChartsOption} from 'echarts';
import {User} from '../../interfaces/user';
import {EchartsUtils} from '../../libraries/echarts-utils';
import {CurrencyService} from '../../services/currency.service';
import {DecimalService} from '../../services/decimal.service';
import {selectMetricComponentObjects} from '../selectors';
import {selectDataSetMetricsGroupsInitialized} from '../dataSetMetricsGroups/dataSetMetricsGroups.selectors';

@Injectable()
export class ChartStore extends ComponentStore<ChartStoreState> {
  private readonly _localStorageKey: string = 'dashboard-params';

  public readonly periods: Array<AdRPeriod> = this._periodsS.getPeriods(false);

  public readonly metricsCtrl: FormControl<MetricComponentObjects> = new FormControl([], [Validators.required]);
  public readonly periodCtrl: FormControl<AdRPeriod> = new FormControl(null, [Validators.required]);
  public readonly formGrp: FormGroup<{ metrics: FormControl<MetricComponentObjects>, period: FormControl<AdRPeriod> }> = new FormGroup({ metrics: this.metricsCtrl, period: this.periodCtrl });

  private readonly _dataSet$: Observable<Serializer<Site>> = this._store.select(selectSite);
  private readonly _user$: Observable<Serializer<User>> = this._store.select(selectUser);
  private readonly _metricsComponentObjects: Observable<MetricComponentObjects> = this._store.select(selectMetricComponentObjects);

  public readonly report$: Observable<AdRReport | null> = this.select((state: ChartStoreState): AdRReport | null => state.report);
  public readonly chartOption$: Observable<EChartsOption> = this.select((state: ChartStoreState): EChartsOption => state.chartOption);
  public readonly loading$: Observable<boolean> = this.select((state: ChartStoreState): boolean => state.loading);

  public readonly load: () => void = this.effect((params$: Observable<void>): Observable<AdRReport> => params$.pipe(
    filter(() => this.metricsCtrl.valid && this.periodCtrl.valid),
    tap((): void => {
      this._initFormValidator();
      this.patchState({
        loading: true
      });
    }),
    withLatestFrom(this._reportStore.requestOptions$, this._dataSet$, this._user$),
    switchMap(([_, options, dataset, user]: [void, Partial<AdRequestOptions>, Serializer<Site>, Serializer<User>]): Observable<AdRReport> => this._service.getReport(
      dataset,
      this.metricsCtrl.value.map((metric: MetricComponentObject): string => metric.payload.attributes.slug),
      this.periodCtrl.value.type,
      options
    ).pipe(
      tap((report: AdRReport): void => {
        this.patchState({
          report: report,
          chartOption: this._getChartOption(user, report),
          loading: false
        });
      }),
      catchError(() => of())
    ))
  ));

  public readonly save: () => void = this.effect((params$: Observable<void>): Observable<boolean> => params$.pipe(
    withLatestFrom(this._dataSet$),
    switchMap(([_, site]: [void, Serializer<Site>]): Observable<boolean> => this._localStorage.setItem(
      `${this._localStorageKey}-${site.id}`,
      {
        metrics: this.metricsCtrl.value.map((metric: MetricComponentObject) => metric.payload),
        period: this.periodCtrl.value
      }
    )),
    tap((): void => {
      this._store.dispatch(showSnackbar({
        message: 'dashboard.save_message',
        icon: 'save'
      }));
    })
  ));

  private readonly _reloadActions$: Subscription = this.effect((): Observable<Partial<AdRequestOptions>> => this._reportStore.requestOptionsChanges$.pipe(
    tap((): void => {
      this.load();
    })
  ));

  private readonly _selectSite$: Subscription = this.effect((): Observable<TypedAction<string>> => this._actions$.pipe(
    ofType(initSiteSuccess),
    tap((): void =>  {
      this.formGrp.reset();

      this.patchState({
        report: null,
        chartOption: {}
      });
    })
  ));

  private readonly _init$: Subscription = this.effect((): Observable<{ metrics: Array<Serializer<Metric>>, period: AdRPeriod } | null> => this._store.select(selectDataSetMetricsGroupsInitialized).pipe(
    filter((initialized: boolean) => initialized),
    withLatestFrom(
      this._dataSet$,
      this._metricsComponentObjects
    ),
    switchMap(([_, dataSet, metricComponentObjects]: [boolean, Serializer<Site>, MetricComponentObjects]): Observable<{ metrics: Array<Serializer<Metric>>, period: AdRPeriod } | null> => this._localStorage.getItem<{ metrics: Array<Serializer<Metric>>, period: AdRPeriod } | null>(`${this._localStorageKey}-${dataSet.id}`)
      .pipe(
        tap((data: { metrics: Array<Serializer<Metric>>, period: AdRPeriod }): void =>  {
          if (!data) {
            data = {
              metrics: [{ attributes: { slug: 'ad-clicks'} } as Serializer<Metric>, { attributes: { slug: 'ad-spend'} } as Serializer<Metric>, { attributes: { slug: 'ad-conversions'} } as Serializer<Metric>],
              period: { type: 'last_30_days' }
            }
          }

          this.metricsCtrl.setValue(
            data.metrics
              .map((metricSerialized: Serializer<Metric>): MetricComponentObject | undefined => metricComponentObjects.find((metricComponentObject: MetricComponentObject): boolean => metricComponentObject.payload.attributes.slug === metricSerialized.attributes.slug))
              .filter((metric: MetricComponentObject | undefined) => metric)
          );
          this.periodCtrl.setValue(this.periods.find((period: AdRPeriod): boolean => period.type === data.period.type));
          this.load();
        })
      ) as Observable<{ metrics: Array<Serializer<Metric>>, period: AdRPeriod } | null>),
  ));

  constructor(
    private readonly _reportStore: ReportStore,
    private readonly _store: Store<AppState>,
    private readonly _actions$: Actions,
    private readonly _service: QuickDashboardService,
    private readonly _localStorage: LocalStorage,
    private readonly _periodsS: PeriodsService,
    private readonly _currencyS: CurrencyService,
    private readonly _decimalS: DecimalService,
  ) {
    super({
      report: null,
      chartOption: {},
      loading: false
    });
  }

  private _initFormValidator(): void {
    this.formGrp.setValidators(FormValidators.formIsValid(this.formGrp.value));
    this.formGrp.updateValueAndValidity();
  }

  private _getChartOption(user: Serializer<User>, data: AdRReport): EChartsOption {
    const xAxisData: Array<string> = [];
    const yAxis: Array<any> = [];
    const series: Array<any> = [];
    const currencies: Array<string> = [];

    for (let i = 0; i < this.formGrp.value['metrics'].length; i++) {
      currencies[i] = (this.formGrp.value['metrics'][i].payload.attributes as DataSetMetric).format_type === 'currency' && data.parameters.currency || null;

      const isCurrency: boolean = (this.formGrp.value['metrics'][i].payload.attributes as DataSetMetric).format_type === 'currency';

      yAxis.push({
        show: true,
        type: 'value',
        //name: this.selectedMetrics[i].attributes.name,
        offset: EchartsUtils.getAxisOffset(i),
        position: i && 'right' || 'left',
        //nameTextStyle: {
        //  padding: [0, 0, i === 2 && (this.selectedMetrics[2].attributes.name.length >= 16 || this.selectedMetrics[1].attributes.name.length >= 16) && 15 || 0, 0]
        //},
        axisLine: {
          show: true,
          lineStyle: {
            color: EchartsUtils.colors[i]
          }
        },
        axisLabel : {
          formatter: value => isCurrency && this._currencyS.format(user, value, data.parameters.currency, 0) || this._decimalS.format(user, value, '0.0')
        }
      });

      series.push({
        name: this.formGrp.value['metrics'][i].payload.attributes.name,
        yAxisIndex: i,
        data: [],
        type: 'line',
        smooth: true
      });
    }

    for (const row of data.rows) {
      xAxisData.push(row.dimensions.day.formatted_value);

      for (let i: number = 0; i < this.formGrp.value['metrics'].length; i++) {
        row.metrics[this.formGrp.value['metrics'][i].payload.attributes.slug].value = row.metrics[this.formGrp.value['metrics'][i].payload.attributes.slug].value || 0;
        row.metrics[this.formGrp.value['metrics'][i].payload.attributes.slug].formatted_value = row.metrics[this.formGrp.value['metrics'][i].payload.attributes.slug].formatted_value || 0;
        series[i].data.push(row.metrics[this.formGrp.value['metrics'][i].payload.attributes.slug]);
      }
    }

    if (xAxisData.length && yAxis.length) {
      return {
        color: EchartsUtils.colors,
        grid: EchartsUtils.getGrid(this.formGrp.value['metrics'].length),
        tooltip: {
          trigger: 'axis',
          formatter: EchartsUtils.tooltipFormatterMultiple,
          show: true,
          axisPointer: {
            type: 'cross',
            label: {
              formatter: params => {
                if (params.axisDimension === 'y') {
                  return currencies[params.axisIndex] && this._currencyS.format(user, params.value as number, currencies[params.axisIndex]) ||
                    this._decimalS.format(user, params.value as number);
                } else {
                  return params.value.toString();
                }
              }
            }
          }
        },
        legend: {
          show: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisData
        },
        yAxis: yAxis,
        series: series
      };
    }

    return {};
  }
}
